import { cloneElement, useState, type MouseEvent, type ReactElement } from 'react'
import { Popover, toast, type ToastOptions } from '@/components/play'
import { inviteError, invitesApi, type ChatPerson, type InviteError } from '@/lib/watch/invitesApi'
import { sentenceName } from '@/lib/watch/rules'
import { useWatchLayout } from '@/lib/watch/useMediaQuery'
import { INVITE_TO_STAGE_TITLE, InviteToStage } from './InviteToStage'
import { WatchSheet } from './WatchSheet'

export interface InviteToStageOpenerProps {
  stageId: string
  /** Seats taken now, hosts included: the stage's live members, as the server counts them for `stage_full`. */
  seatsTaken: number
  /** The named button that opens it (an IconButton): the + face, or the dock's Invite to stage. */
  trigger: ReactElement<{ onClick?: (event: MouseEvent<HTMLElement>) => void }>
  side?: 'top' | 'bottom'
  align?: 'start' | 'center' | 'end'
  /** Told when the panel opens and closes, so the + face can stay while it is open (a seat taken meanwhile). */
  onOpenChange?(open: boolean): void
}

/**
 * Opens "Invite to your stage" (`InviteToStage`) from its trigger, as the prototype's `openInvite`: on the desktop a Play
 * Popover anchored to the trigger (above it, by default: the + face and the dock sit at the canvas's foot); on a phone,
 * upright or on its side, a sheet from the bottom (`WatchSheet`). The panel mounts only while open, so its stage link
 * loads when it is asked for.
 */
export function InviteToStageOpener({ stageId, seatsTaken, trigger, side = 'top', align = 'start', onOpenChange }: InviteToStageOpenerProps) {
  const desktop = useWatchLayout() === 'desktop'
  const [open, setOpenState] = useState(false)
  const setOpen = (next: boolean) => {
    setOpenState(next)
    onOpenChange?.(next)
  }
  const panel = <InviteToStage stageId={stageId} seatsTaken={seatsTaken} />

  if (desktop) {
    return (
      <Popover
        title={INVITE_TO_STAGE_TITLE}
        trigger={trigger}
        side={side}
        align={align}
        onOpenChange={(next) => onOpenChange?.(next)}
        className="watch-invite-popover"
      >
        {panel}
      </Popover>
    )
  }

  const opener = cloneElement(trigger, {
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
    onClick: (event: MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(event)
      setOpen(true)
    },
  } as Record<string, unknown>)
  return (
    <>
      {opener}
      <WatchSheet open={open} onOpenChange={setOpen} title={INVITE_TO_STAGE_TITLE}>
        {panel}
      </WatchSheet>
    </>
  )
}

/** What a refused invite says, in the toast that answers it. */
function inviteRefusal(error: InviteError, who: string): ToastOptions {
  switch (error.type) {
    case 'stage_full':
      return { tone: 'error', title: 'Stage full', description: `Remove someone to invite ${who}.` }
    // Live on another stage of the event, or a host of this one: nothing to do, so not an error.
    case 'already_on_stage':
      return { title: `${who} is on stage already` }
    default:
      return { tone: 'error', title: `Couldn’t invite ${who}`, description: error.message }
  }
}

/**
 * A host's Invite on stage, from a chat message (`POST /stages/:id/guest_invitations`): the person becomes an invited
 * guest, and the server posts the invite card in the chat. A toast answers either way; it never rejects. Nothing on the
 * stage changes here: their face comes when they join (the room's metadata says so).
 */
export async function inviteOnStage(stageId: string, person: ChatPerson): Promise<void> {
  const who = sentenceName(person)
  try {
    await invitesApi.inviteGuest(stageId, person.id)
    toast({ title: `Invited ${who} on stage` })
  } catch (failure) {
    toast(inviteRefusal(inviteError(failure), who))
  }
}
